import { useEffect, useRef } from 'react';

export const useVoiceRecognitionMonitor = (
    voiceRecordingRef: React.RefObject<any>,
    lastFocusedRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null>,
    shouldRestartVoice: () => boolean,
    restartVoiceRecognition: (reason?: string) => Promise<boolean>
) => {
    const monitorIntervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const startMonitoring = () => {
            if (monitorIntervalRef.current) {
                clearInterval(monitorIntervalRef.current);
            }

            monitorIntervalRef.current = setInterval(() => {
                const focused = document.activeElement as HTMLElement;
                const expected = lastFocusedRef.current;

                // Restore focus if it was lost unexpectedly
                if (expected && (!focused || focused === document.body || focused === document.documentElement)) {
                    expected.focus();
                }

                // Check for voice recognition timeout and auto-restart
                if (shouldRestartVoice()) {
                    restartVoiceRecognition('timeout');
                }

                // Periodic status logging (every 10 seconds)
                if (Date.now() % 10000 < 100) {
                    console.log('Voice status:', {
                        isListening: voiceRecordingRef.current?.isListening?.(),
                        hasFocus: !!lastFocusedRef.current,
                        activeElement: document.activeElement?.tagName
                    });
                }
            }, 100);
        };

        const stopMonitoring = () => {
            if (monitorIntervalRef.current) {
                clearInterval(monitorIntervalRef.current);
                monitorIntervalRef.current = null;
            }
        };

        // Start monitoring when voice recognition is active
        if (voiceRecordingRef.current?.isListening?.()) {
            startMonitoring();
        } else {
            stopMonitoring();
        }

        return stopMonitoring;
    }, [voiceRecordingRef.current?.isListening?.(), shouldRestartVoice, restartVoiceRecognition, lastFocusedRef]);

    return {
        // Expose cleanup function if needed
        cleanup: () => {
            if (monitorIntervalRef.current) {
                clearInterval(monitorIntervalRef.current);
                monitorIntervalRef.current = null;
            }
        }
    };
};
